import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaGavel, FaBuilding, FaBalanceScale, FaUserTie,
  FaShieldAlt, FaClipboardCheck, FaUserCheck,
  FaPhoneAlt, FaUserShield, FaBriefcase, FaSearch,
  FaFilter, FaHandshake
} from 'react-icons/fa';
import './AddressBook.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AddressBook = () => {
  const [stats, setStats] = useState({
    courts: 0,
    jails: 0,
    prosecutingAttorneys: 0,
    publicDefenders: 0,
    sheriffOffices: 0,
    pretrialServices: 0,
    probationParole: 0,
    dispatchCenters: 0,
    policeDepartments: 0,
    privateAttorneys: 0,
    bailAgencies: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [counties, setCounties] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState('');
  const navigate = useNavigate();

  const categories = [
    {
      key: 'courts',
      name: 'Courts',
      icon: FaGavel,
      description: 'District, Justice, Municipal',
      color: '#2c3e50',
      path: '/address-book/courts'
    },
    {
      key: 'jails',
      name: 'Jails & Detention Centers',
      icon: FaBuilding,
      description: 'County Jails, Detention Facilities',
      color: '#34495e',
      path: '/address-book/jails'
    },
    {
      key: 'prosecutingAttorneys',
      name: 'County Attorneys',
      icon: FaBalanceScale,
      description: 'Montana County Attorney Offices',
      color: '#c0392b',
      path: '/address-book/county-attorneys'
    },
    {
      key: 'publicDefenders',
      name: 'Public Defenders',
      icon: FaUserTie,
      description: 'Public Defense Offices',
      color: '#16a085',
      path: '/address-book/public-defenders'
    },
    {
      key: 'sheriffOffices',
      name: "Sheriff's Offices",
      icon: FaShieldAlt,
      description: 'County Sheriff Departments',
      color: '#2980b9',
      path: '/address-book/sheriff-offices'
    },
    {
      key: 'pretrialServices',
      name: 'Pretrial Services',
      icon: FaClipboardCheck,
      description: 'Pretrial Services Offices',
      color: '#8e44ad',
      path: '/address-book/pretrial-services'
    },
    {
      key: 'probationParole',
      name: 'Probation & Parole',
      icon: FaUserCheck,
      description: 'Probation & Parole Offices',
      color: '#d35400',
      path: '/address-book/probation-parole'
    },
    {
      key: 'dispatchCenters',
      name: 'Dispatch Centers',
      icon: FaPhoneAlt,
      description: '911 & Emergency Communications',
      color: '#e74c3c',
      path: '/address-book/dispatch-centers'
    },
    {
      key: 'policeDepartments',
      name: 'Police Departments',
      icon: FaUserShield,
      description: 'City, Municipal, Tribal Police',
      color: '#27ae60',
      path: '/address-book/police-departments'
    },
    {
      key: 'privateAttorneys',
      name: 'Private Attorneys',
      icon: FaBriefcase,
      description: 'Criminal Defense Attorneys',
      color: '#7f8c8d',
      path: '/address-book/private-attorneys'
    },
    {
      key: 'bailAgencies',
      name: 'Bail Bond Agencies',
      icon: FaHandshake,
      description: 'Licensed Bail Bond Companies',
      color: '#f39c12',
      path: '/address-book/bail-agencies'
    }
  ];

  useEffect(() => {
    fetchStats();
    fetchCounties();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/address-book/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      } else {
        setError('Failed to load statistics');
      }
    } catch (err) {
      console.error('Error fetching address book stats:', err);
      setError('Failed to load address book statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchCounties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/address-book/counties`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setCounties(data.data);
      }
    } catch (err) {
      console.error('Error fetching counties:', err);
    }
  };

  const handleCategoryClick = (path) => {
    console.log('=== AddressBook Navigation Debug ===');
    console.log('Path clicked:', path);
    console.log('Current location:', window.location.pathname);
    console.log('Navigate function:', typeof navigate);

    if (path) {
      console.log('About to navigate to:', path);
      try {
        navigate(path);
        console.log('Navigation called successfully');
      } catch (error) {
        console.error('Navigation error:', error);
      }
    } else {
      console.log('No path provided');
    }
  };

  const handleCountyChange = (e) => {
    const county = e.target.value;
    setSelectedCounty(county);
    if (county) {
      navigate(`/address-book/county/${county}`);
    }
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="address-book-container">
        <div className="loading">Loading Address Book...</div>
      </div>
    );
  }

  return (
    <div className="address-book-container">
      <div className="address-book-header">
        <div className="header-content">
          <h1>Montana Justice System Address Book</h1>
          <p className="subtitle">Quick access to courts, agencies, and legal professionals</p>
        </div>
        <div className="header-right">
          <div className="header-stats">
            <div className="total-count">
              <span className="count-number">{stats.total}</span>
              <span className="count-label">Total Organizations</span>
            </div>
          </div>
          <div className="header-county-filter">
            <FaFilter className="filter-icon" />
            <select
              value={selectedCounty}
              onChange={handleCountyChange}
              className="county-select-header"
            >
              <option value="">Filter by County</option>
              {counties.map((county) => (
                <option key={county} value={county}>
                  {county} County
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="address-book-controls">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="categories-grid">
        {filteredCategories.map((category) => {
          const Icon = category.icon;
          const count = stats[category.key] || 0;

          return (
            <div
              key={category.key}
              className={`category-card ${!category.path ? 'non-clickable' : ''}`}
              onClick={() => handleCategoryClick(category.path)}
              style={{ '--category-color': category.color }}
            >
              <div className="category-icon-wrapper">
                <Icon className="category-icon" />
              </div>
              <div className="category-content">
                <h3 className="category-name">{category.name}</h3>
                <p className="category-description">{category.description}</p>
                <div className="category-count">
                  <span className="count">{count}</span>
                  <span className="count-text">
                    {category.key === 'prosecutingAttorneys' ? 'offices' : 'organizations'}
                  </span>
                </div>
              </div>
              {category.path && <div className="category-arrow">→</div>}
            </div>
          );
        })}
      </div>

      {filteredCategories.length === 0 && (
        <div className="no-results">
          <FaSearch size={48} />
          <p>No categories match your search</p>
        </div>
      )}
    </div>
  );
};

export default AddressBook;
